import Phaser from 'phaser';
import {
  LONG_PRESS_DELAY_MS,
  LONG_PRESS_FAST_REPEAT_MS,
  LONG_PRESS_MS,
  LONG_PRESS_REPEAT_MS,
  LONG_PRESS_SLOP_PX,
} from '../config.js';

export interface PressGestureOptions {
  scene: Phaser.Scene;
  /** Game object that already has `setInteractive(...)` applied. */
  target: Phaser.GameObjects.GameObject;
  /** Where the progress ring is centered (scene coords). */
  ringCenter: { x: number; y: number };
  /** Outer radius of the progress ring in canvas-buffer pixels. */
  ringRadius: number;
  /** Stroke width of the ring. */
  ringStroke: number;
  /** Called on a quick tap (no long-press). */
  onTap: () => void;
  /**
   * Called when the long-press fires (initial fire and repeat fires while
   * still held). Skipped entirely if `canLongPress()` returned false at press
   * start.
   */
  onLongPress: () => void;
  /**
   * Returns whether long-press is meaningful right now. If false at press
   * start, the progress ring is suppressed and `onLongPress` is never called
   * — but `onTap` still fires on release. Typical use: only enable
   * long-press when there's a wager to remove.
   */
  canLongPress: () => boolean;
}

export interface PressGestureHandle {
  destroy(): void;
  startHinting(): void;
  stopHinting(): void;
}

/**
 * Wires click + long-press behavior on an interactive game object, with a
 * progress ring centered on `ringCenter`. Long-press auto-repeats while held
 * (slow first repeat, faster after) so users can pull chips off a stack
 * smoothly. Right-click acts as an immediate long-press equivalent.
 *
 * Used by craps' BetZone to drive the press-and-hold chip gesture.
 */
export function attachPressGesture(opts: PressGestureOptions): PressGestureHandle {
  let pressStart: { x: number; y: number } | undefined;
  let pressDelayTimer: Phaser.Time.TimerEvent | undefined;
  let pressTimer: Phaser.Time.TimerEvent | undefined;
  let repeatTimer: Phaser.Time.TimerEvent | undefined;
  let progressRing: Phaser.GameObjects.Graphics | undefined;
  let progressTween: Phaser.Tweens.Tween | undefined;
  let progressPulse: Phaser.Tweens.Tween | undefined;
  let hintGfx: Phaser.GameObjects.Graphics | undefined;
  let hintTween: Phaser.Tweens.Tween | undefined;
  const progressState = { angle: 0 };
  let suppressNextTap = false;

  const drawProgressRing = () => {
    if (!progressRing) return;
    const r = opts.ringRadius;
    const stroke = opts.ringStroke;
    progressRing.clear();
    // Soft halo so the ring stands out against the felt even past a thumb.
    progressRing.fillStyle(0x000000, 0.35);
    progressRing.fillCircle(0, 0, r + stroke);
    progressRing.lineStyle(stroke, 0xffffff, 0.4);
    progressRing.strokeCircle(0, 0, r);
    progressRing.lineStyle(stroke, 0xff5544, 1);
    progressRing.beginPath();
    progressRing.arc(0, 0, r, -Math.PI / 2, -Math.PI / 2 + progressState.angle, false);
    progressRing.strokePath();
  };

  const clearProgressRing = () => {
    if (progressTween) { progressTween.stop(); progressTween = undefined; }
    if (progressPulse) { progressPulse.stop(); progressPulse = undefined; }
    if (progressRing) { progressRing.destroy(); progressRing = undefined; }
  };

  const cancelPress = () => {
    pressStart = undefined;
    if (pressDelayTimer) { pressDelayTimer.remove(false); pressDelayTimer = undefined; }
    if (pressTimer) { pressTimer.remove(false); pressTimer = undefined; }
    if (repeatTimer) { repeatTimer.remove(false); repeatTimer = undefined; }
    clearProgressRing();
  };

  const startPress = (pointer: Phaser.Input.Pointer) => {
    cancelPress();
    pressStart = { x: pointer.x, y: pointer.y };
    if (!opts.canLongPress()) return;

    pressDelayTimer = opts.scene.time.delayedCall(LONG_PRESS_DELAY_MS, () => {
      pressDelayTimer = undefined;
      progressState.angle = 0;
      progressRing = opts.scene.add.graphics();
      progressRing.setPosition(opts.ringCenter.x, opts.ringCenter.y);
      drawProgressRing();

      progressTween = opts.scene.tweens.add({
        targets: progressState,
        angle: Math.PI * 2,
        duration: LONG_PRESS_MS,
        onUpdate: () => drawProgressRing(),
      });
      progressRing.setScale(1);
      progressPulse = opts.scene.tweens.add({
        targets: progressRing,
        scale: { from: 1, to: 1.06 },
        duration: 320,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });

      pressTimer = opts.scene.time.delayedCall(LONG_PRESS_MS, () => {
        suppressNextTap = true;
        pressStart = undefined;
        clearProgressRing();
        opts.onLongPress();

        repeatTimer = opts.scene.time.delayedCall(LONG_PRESS_REPEAT_MS, () => {
          repeatTimer = undefined;
          opts.onLongPress();
          repeatTimer = opts.scene.time.addEvent({
            delay: LONG_PRESS_FAST_REPEAT_MS,
            loop: true,
            callback: () => opts.onLongPress(),
          });
        });
      });
    });
  };

  const onPointerDown = (pointer: Phaser.Input.Pointer) => {
    // Right-click acts as an immediate long-press equivalent (desktop ergonomics).
    if (pointer.rightButtonDown()) {
      if (!opts.canLongPress()) return;
      suppressNextTap = true;
      opts.onLongPress();
      return;
    }
    startPress(pointer);
  };

  const onPointerMove = (pointer: Phaser.Input.Pointer) => {
    if (!pressStart) return;
    const dx = pointer.x - pressStart.x;
    const dy = pointer.y - pressStart.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_SLOP_PX) cancelPress();
  };

  const onPointerUp = () => {
    const wasTap = pressStart !== undefined;
    cancelPress();
    if (suppressNextTap) { suppressNextTap = false; return; }
    if (wasTap) opts.onTap();
  };

  opts.target.on('pointerdown', onPointerDown);
  opts.target.on('pointermove', onPointerMove);
  opts.target.on('pointerup', onPointerUp);
  opts.target.on('pointerout', cancelPress);

  const startHinting = () => {
    if (hintGfx) return;
    const g = opts.scene.add.graphics();
    g.lineStyle(opts.ringStroke, 0xd4af37, 1);
    g.strokeCircle(opts.ringCenter.x, opts.ringCenter.y, opts.ringRadius);
    hintGfx = g;
    hintTween = opts.scene.tweens.add({
      targets: g,
      alpha: { from: 0.3, to: 1.0 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  };

  const stopHinting = () => {
    hintTween?.stop();
    hintTween = undefined;
    hintGfx?.destroy();
    hintGfx = undefined;
  };

  return {
    destroy() {
      stopHinting();
      cancelPress();
      opts.target.off('pointerdown', onPointerDown);
      opts.target.off('pointermove', onPointerMove);
      opts.target.off('pointerup', onPointerUp);
      opts.target.off('pointerout', cancelPress);
    },
    startHinting,
    stopHinting,
  };
}
